import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/auth/AuthContext";
import StickyOrderBar from "@/components/landing/StickyOrderBar";
import CheckoutModal from "@/components/landing/CheckoutModal";
import SuccessModal from "@/components/landing/SuccessModal";
import AuthModal from "@/auth/AuthModal";
import {
    FALLBACK_MEALS,
    FALLBACK_DELIVERY_FEE,
    FALLBACK_FAMILY_CONFIG,
} from "@/cart/fallbackMeals";

const CartContext = createContext(null);

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside CartProvider");
    return ctx;
};

export function CartProvider({ children }) {
    const { user } = useAuth();
    // Initialize with bundled fallback so menu ALWAYS renders, even if the
    // API is unreachable (CORS, network, deploy gap, backend down).
    const [meals, setMeals] = useState(FALLBACK_MEALS);
    const [deliveryFee, setDeliveryFee] = useState(FALLBACK_DELIVERY_FEE);
    const [familyConfig, setFamilyConfig] = useState(FALLBACK_FAMILY_CONFIG);
    const [cart, setCart] = useState({});
    const [familyCart, setFamilyCart] = useState([]);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [authOpen, setAuthOpen] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [pollingSession, setPollingSession] = useState(null);
    const [loadingPayment, setLoadingPayment] = useState(false);
    const [initialSubscribe, setInitialSubscribe] = useState(false);

    // Load fresh meals from API. On failure we keep the fallback silently —
    // no error toast, because the menu is already showing correctly.
    useEffect(() => {
        let alive = true;
        axios
            .get(`${API}/meals`)
            .then((res) => {
                if (!alive) return;
                if (res.data?.meals?.length) setMeals(res.data.meals);
                if (res.data?.delivery_fee != null)
                    setDeliveryFee(res.data.delivery_fee);
                if (res.data?.family_plate)
                    setFamilyConfig(res.data.family_plate);
            })
            .catch((e) => {
                // Silent — fallback meals are already rendering. Log for debugging.
                console.warn(
                    "meals API unavailable, using bundled fallback menu",
                    e?.message || e,
                );
            });
        return () => {
            alive = false;
        };
    }, []);

    // Hydrate cart from sessionStorage (re-order / plan re-order)
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("reorder_cart");
            if (raw) {
                const stored = JSON.parse(raw);
                if (stored && typeof stored === "object") setCart(stored);
                sessionStorage.removeItem("reorder_cart");
                const subFlag =
                    sessionStorage.getItem("reorder_subscribe") === "1";
                sessionStorage.removeItem("reorder_subscribe");
                if (subFlag) {
                    setInitialSubscribe(true);
                    setTimeout(() => setCheckoutOpen(true), 500);
                }
            }
        } catch {
            /* ignore */
        }
    }, []);

    // Resolve the customer's email if we know it (signed-in OR they subscribed
    // to the welcome popup). Used for abandoned-cart tracking.
    const knownEmail = useMemo(() => {
        if (user?.email) return user.email;
        try {
            const raw = localStorage.getItem("cmp_popup_state_v1");
            if (raw) {
                const s = JSON.parse(raw);
                if (s?.subscribedEmail) return s.subscribedEmail;
            }
        } catch {
            /* ignore */
        }
        return null;
    }, [user]);

    // Stripe return detection
    useEffect(() => {
        const url = new URL(window.location.href);
        const sid = url.searchParams.get("session_id");
        const canceled = url.searchParams.get("canceled");
        if (canceled) {
            toast.info("Payment canceled. Your cart is still here.");
            const clean = window.location.origin + window.location.pathname;
            window.history.replaceState({}, "", clean);
        }
        if (sid) setPollingSession(sid);
    }, []);

    // Poll payment status
    useEffect(() => {
        if (!pollingSession) return;
        let attempts = 0;
        let cancelled = false;
        const maxAttempts = 8;
        const poll = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                const res = await axios.get(
                    `${API}/checkout/status/${pollingSession}`
                );
                const { payment_status, status } = res.data;
                if (payment_status === "paid") {
                    setSuccessData(res.data);
                    setCart({});
                    setFamilyCart([]);
                    setPollingSession(null);
                    const clean =
                        window.location.origin + window.location.pathname;
                    window.history.replaceState({}, "", clean);
                    return;
                }
                if (status === "expired") {
                    toast.error("Payment session expired.");
                    setPollingSession(null);
                    return;
                }
                if (attempts < maxAttempts) {
                    setTimeout(poll, 2000);
                } else {
                    toast.message(
                        "Payment is still processing. Check your email for confirmation."
                    );
                    setPollingSession(null);
                }
            } catch (e) {
                if (attempts < maxAttempts) setTimeout(poll, 2000);
                else setPollingSession(null);
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [pollingSession]);

    const updateQty = useCallback((mealId, qty) => {
        setCart((prev) => {
            const next = { ...prev };
            if (qty <= 0) delete next[mealId];
            else next[mealId] = qty;
            return next;
        });
    }, []);

    const addFamilyMeal = useCallback(
        ({ meal_id, meal_name, members, price }) => {
            if (!meal_id || !price) return;
            setFamilyCart((prev) => [
                ...prev,
                {
                    id: `fam_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                    meal_id,
                    meal_name,
                    members,
                    price,
                },
            ]);
        },
        []
    );

    const removeFamilyMeal = useCallback((id) => {
        setFamilyCart((prev) => prev.filter((f) => f.id !== id));
    }, []);

    const summary = useMemo(() => {
        let count = 0;
        let singleMealQty = 0;
        let singleMealSubtotal = 0;
        let subtotal = 0;
        const lines = [];
        for (const [id, qty] of Object.entries(cart)) {
            const meal = meals.find((m) => m.id === id);
            if (!meal) continue;
            count += qty;
            singleMealQty += qty;
            const line = +(meal.price * qty).toFixed(2);
            subtotal += line;
            singleMealSubtotal += line;
            lines.push({
                kind: "meal",
                key: meal.id,
                meal,
                name: meal.name,
                qty,
                line,
            });
        }
        for (const fm of familyCart) {
            count += 1;
            subtotal += fm.price;
            lines.push({
                kind: "family",
                key: fm.id,
                name: `Family · ${fm.meal_name} · serves ${fm.members}`,
                qty: 1,
                line: fm.price,
                members: fm.members,
                meal_id: fm.meal_id,
            });
        }
        // Volume tier discount on single meals only (matches backend logic)
        let volumePct = 0;
        if (singleMealQty >= 12) volumePct = 20;
        else if (singleMealQty >= 9) volumePct = 15;
        else if (singleMealQty >= 6) volumePct = 10;
        const volumeDiscount = +(singleMealSubtotal * (volumePct / 100)).toFixed(2);
        const hasFamily = familyCart.length > 0;
        const minSingleMeals = 3;
        const meetsMinimum = hasFamily || singleMealQty >= minSingleMeals;
        const total = +(
            subtotal - volumeDiscount + (count > 0 ? deliveryFee : 0)
        ).toFixed(2);
        return {
            count,
            singleMealQty,
            singleMealSubtotal: +singleMealSubtotal.toFixed(2),
            subtotal: +subtotal.toFixed(2),
            volumePct,
            volumeDiscount,
            minSingleMeals,
            meetsMinimum,
            total,
            lines,
            deliveryFee,
        };
    }, [cart, familyCart, meals, deliveryFee]);

    // Snapshot the cart to the backend whenever it changes (debounced).
    // This is what powers the 1-hour abandoned-cart email recovery.
    // Declared AFTER `summary` so we don't hit a TDZ on initial render.
    useEffect(() => {
        if (!knownEmail) return;
        if (!summary.count || summary.subtotal <= 0) return;
        const items = summary.lines.map((l) => ({
            name: l.name || l.meal?.name || "Meal",
            quantity: l.qty,
            line_total: l.line,
            meal_id: l.meal?.id || l.meal_id || null,
        }));
        const handle = setTimeout(() => {
            axios
                .post(`${API}/marketing/track-cart`, {
                    email: knownEmail,
                    items,
                    subtotal: summary.subtotal,
                    currency: "usd",
                })
                .catch(() => {
                    /* silent — recovery is best-effort */
                });
        }, 3000);
        return () => clearTimeout(handle);
    }, [knownEmail, summary.count, summary.subtotal, summary.lines]);

    const startCheckout = async (form) => {
        if (summary.count === 0) {
            toast.error("Add at least one meal to your order.");
            return;
        }
        setLoadingPayment(true);
        try {
            const items = Object.entries(cart).map(([meal_id, quantity]) => ({
                meal_id,
                quantity,
            }));
            const family_meals = familyCart.map((f) => ({
                meal_id: f.meal_id,
                members: f.members,
                quantity: 1,
            }));
            const token = localStorage.getItem("session_token");
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.post(
                `${API}/checkout/session`,
                {
                    items,
                    family_meals,
                    origin_url: window.location.origin,
                    customer_name: form.name,
                    customer_email: form.email,
                    customer_phone: form.phone,
                    delivery_address: form.address,
                    delivery_date: form.delivery_date,
                    notes: form.notes || "",
                    subscribe: !!form.subscribe,
                    promo_code: form.promo_code || null,
                    payment_plan: form.payment_plan || "full",
                    deposit_percent: form.deposit_percent || 100,
                },
                { headers }
            );
            const url = res.data?.session_url;
            if (!url) throw new Error("No checkout URL");
            window.location.href = url;
        } catch (e) {
            console.error(e);
            toast.error(
                e?.response?.data?.detail ||
                    "Couldn't start checkout. Try again."
            );
            setLoadingPayment(false);
        }
    };

    const openCheckout = useCallback(() => {
        if (!summary.meetsMinimum) {
            toast.error(
                `Minimum order is ${summary.minSingleMeals} plates. You have ${summary.singleMealQty}. Add ${summary.minSingleMeals - summary.singleMealQty} more to check out.`,
            );
            return;
        }
        setCheckoutOpen(true);
    }, [summary.meetsMinimum, summary.minSingleMeals, summary.singleMealQty]);
    const openAuth = useCallback(() => setAuthOpen(true), []);

    const value = {
        meals,
        familyConfig,
        deliveryFee,
        cart,
        familyCart,
        summary,
        updateQty,
        addFamilyMeal,
        removeFamilyMeal,
        openCheckout,
        openAuth,
        user,
    };

    return (
        <CartContext.Provider value={value}>
            {children}

            <StickyOrderBar
                count={summary.count}
                total={summary.total}
                onCheckout={openCheckout}
            />

            <CheckoutModal
                open={checkoutOpen}
                onOpenChange={setCheckoutOpen}
                summary={summary}
                loading={loadingPayment || !!pollingSession}
                onSubmit={startCheckout}
                canSubscribe={!!user}
                initialSubscribe={initialSubscribe}
                onAuthRequired={() => setAuthOpen(true)}
                onRemoveFamilyItem={removeFamilyMeal}
                prefill={
                    user
                        ? {
                              name: user.name || "",
                              email: user.email || "",
                              phone: user.phone || "",
                          }
                        : null
                }
            />

            <AuthModal open={authOpen} onOpenChange={setAuthOpen} />

            <SuccessModal
                data={successData}
                onClose={() => setSuccessData(null)}
            />

            {pollingSession && !successData && (
                <div
                    data-testid="payment-polling-overlay"
                    className="fixed inset-0 z-[60] bg-navy/80 backdrop-blur-sm flex items-center justify-center px-6"
                >
                    <div className="bg-white rounded-3xl p-8 max-w-md text-center shadow-2xl">
                        <div className="text-5xl mb-4">🌴</div>
                        <h3 className="font-serif text-2xl text-navy mb-2">
                            Confirming your order…
                        </h3>
                        <p className="text-muted2">
                            Hold tight — we're verifying your payment with our
                            kitchen.
                        </p>
                        <div className="mt-6 h-1.5 bg-mint rounded-full overflow-hidden">
                            <div className="h-full bg-forest animate-pulse w-2/3" />
                        </div>
                    </div>
                </div>
            )}
        </CartContext.Provider>
    );
}
